'use client'
import { motion, type Variants } from 'framer-motion'
import { EDIForm } from '@/components/Forms/EDIForm'

// SVG uses viewBox="0 0 100 100" — coordinates map 1:1 from the old percentage values
const NODE_POSITIONS = [
  { cx: 8,  cy: 20 },
  { cx: 28, cy: 12 },
  { cx: 50, cy: 8  },
  { cx: 72, cy: 15 },
  { cx: 92, cy: 22 },
  { cx: 15, cy: 55 },
  { cx: 85, cy: 60 },
  { cx: 38, cy: 88 },
  { cx: 62, cy: 85 },
]

const PATHS = [
  'M 8 20 L 28 12 L 50 8',
  'M 50 8 L 72 15 L 92 22',
  'M 8 20 L 15 55',
  'M 92 22 L 85 60',
  'M 15 55 L 38 88 L 62 85 L 85 60',
  'M 28 12 L 38 88',
  'M 72 15 L 62 85',
]

const pulseVariants: Variants = {
  animate: {
    opacity: [0.3, 0.8, 0.3],
    scale: [1, 1.4, 1],
    transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
  },
}

function streamVariants(delay: number): Variants {
  return {
    animate: {
      strokeDashoffset: [200, 0],
      opacity: [0, 0.6, 0],
      transition: { duration: 2.2, delay, repeat: Infinity, ease: 'linear' },
    },
  }
}

const headingVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
}
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

export function EDISection() {
  return (
    <section id="edi" className="relative overflow-hidden py-24 bg-[color:var(--bg-base)]">
      {/* Ambient glow orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#0ea5e9]/8 blur-[120px] pointer-events-none" aria-hidden />
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#06b6d4]/6 blur-[100px] pointer-events-none" aria-hidden />

      {/* Animated circuit/data-stream layer */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        {/* Static connection lines */}
        {PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="#0ea5e9"
            strokeWidth="1"
            opacity="0.08"
            strokeLinecap="round"
          />
        ))}

        {/* Animated data-packet streams */}
        {PATHS.map((d, i) => (
          <motion.path
            key={`stream-${i}`}
            d={d}
            fill="none"
            stroke="#22d3ee"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="40 200"
            variants={streamVariants(i * 0.35)}
            animate="animate"
          />
        ))}

        {/* Connection nodes */}
        {NODE_POSITIONS.map(({ cx, cy }, i) => (
          <motion.circle
            key={`node-${i}`}
            cx={cx}
            cy={cy}
            r="3"
            fill="#0ea5e9"
            variants={pulseVariants}
            animate="animate"
            initial={{ opacity: 0.3, scale: 1 }}
            transition={{ delay: i * 0.3 } as never}
          />
        ))}
      </svg>

      {/* Subtle dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, #0ea5e9 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden
      />

      <div className="relative z-10 page-container">
        <motion.div
          className="text-center mb-12"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <motion.p
            variants={itemVariants}
            className="text-sm font-semibold tracking-widest uppercase text-[color:var(--accent)] opacity-80 mb-3"
          >
            Platform Integration
          </motion.p>
          <motion.h2
            variants={itemVariants}
            className="text-3xl md:text-5xl font-bold text-white tracking-tight"
          >
            EDI Integration Inquiry
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="mt-4 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed"
          >
            Connect your TMS or load board directly to our dispatch system.
            Supports 204, 214, 990, and 997 transaction sets.
          </motion.p>
        </motion.div>

        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
        >
          <EDIForm />
        </motion.div>
      </div>
    </section>
  )
}
